/**
 * React hook for real-time experience synchronization.
 *
 * Provides a convenient interface for syncing ephemeral UI state between dealer and customer
 * without involving session state or database writes.
 *
 * @example
 * ```typescript
 * // In a React component
 * const { state, send, isConnected } = useExperienceSync<IExperienceSyncState>({
 *   sessionId,
 *   initialState: { sliderPercentage: 50, sliderEnabled: false, swapEnabled: false },
 *   schema: experienceSyncSchema,
 *   isHost: user.type === 'dealer',
 * });
 *
 * // Render based on synced state
 * <Slider value={state.sliderPercentage} onChange={(v) => send({ sliderPercentage: v })} />
 * ```
 */

import * as React from 'react';
import { z } from 'zod';
import { createExperienceSync, IExperienceSyncChannel } from './experienceSync';
import logger from 'utils/log';

export interface IUseExperienceSyncOptions<T extends Record<string, unknown>> {
  /** Session ID to scope the sync channel */
  sessionId: string | undefined;
  /** Topic ID for the sync channel (defaults to 'experience') */
  topicId?: string;
  /** Initial state */
  initialState: T;
  /** Zod schema for validation */
  schema?: z.ZodType<T>;
  /** Whether this client is the host (dealer) that manages state */
  isHost?: boolean;
  /** Auto-connect on mount (defaults to true) */
  autoConnect?: boolean;
  /** Callback when state changes */
  onStateChange?: (state: T) => void;
}

export interface IUseExperienceSyncReturn<T> {
  /** Current synced state */
  state: T;
  /** Send partial state update to peers */
  send: (data: Partial<T>) => void;
  /** Whether connected to the sync channel */
  isConnected: boolean;
  /** Connection ID */
  connectionId: string | undefined;
  /** Manually connect to sync channel */
  connect: () => Promise<string | undefined>;
  /** Manually disconnect from sync channel */
  disconnect: () => void;
  /** Update local state without sending to peers */
  setLocalState: React.Dispatch<React.SetStateAction<T>>;
}

/**
 * React hook for real-time experience synchronization.
 */
export function useExperienceSync<T extends Record<string, unknown>>(
  options: IUseExperienceSyncOptions<T>
): IUseExperienceSyncReturn<T> {
  const {
    sessionId,
    topicId = 'experience',
    initialState,
    schema,
    isHost = false,
    autoConnect = true,
    onStateChange,
  } = options;

  const [state, setState] = React.useState<T>(initialState);
  const [isConnected, setIsConnected] = React.useState(false);
  const [connectionId, setConnectionId] = React.useState<string | undefined>();

  // Keep a ref to the sync instance to avoid recreating on every render
  const syncRef = React.useRef<IExperienceSyncChannel<T> | null>(null);
  // Track the latest state for snapshot responses
  const stateRef = React.useRef<T>(state);
  stateRef.current = state;

  // Memoize callbacks to prevent unnecessary effect triggers
  const onStateChangeRef = React.useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  // Create or update sync instance when sessionId changes
  React.useEffect(() => {
    if (!sessionId) {
      // No session, clean up existing connection
      if (syncRef.current) {
        syncRef.current.disconnect();
        syncRef.current = null;
        setIsConnected(false);
        setConnectionId(undefined);
      }
      return;
    }

    // Create new sync instance
    const sync = createExperienceSync<T>({
      sessionId,
      topicId,
      schema,
      onConnect: (id) => {
        setConnectionId(id);
        setIsConnected(true);
        logger.log(`[useExperienceSync] Connected: ${id}`);
      },
      onDisconnect: () => {
        setConnectionId(undefined);
        setIsConnected(false);
        logger.log('[useExperienceSync] Disconnected');
      },
      onError: (error) => {
        logger.error('[useExperienceSync] Error:', error);
      },
    });

    // Set up event listeners
    sync.on('sync', (data) => {
      setState((prev) => {
        const newState = { ...prev, ...data };
        onStateChangeRef.current?.(newState);
        return newState;
      });
    });

    sync.on('snapshot', (data) => {
      setState(data as T);
      onStateChangeRef.current?.(data as T);
    });

    // Host responds to snapshot requests
    if (isHost) {
      sync.on('snapshotRequest', (from) => {
        logger.log(`[useExperienceSync] Snapshot requested by: ${from}`);
        sync.sendSnapshot(stateRef.current);
      });

      // Host proactively sends snapshot when a new peer connects
      sync.on('peerConnected', (peerConnectionId) => {
        logger.log(`[useExperienceSync] New peer connected: ${peerConnectionId}, sending snapshot`);
        // Small delay to ensure the new peer has finished setting up listeners
        setTimeout(() => {
          sync.sendSnapshot(stateRef.current);
        }, 150);
      });
    }

    syncRef.current = sync;

    // Auto-connect if enabled
    if (autoConnect) {
      sync.connect().catch((error) => {
        logger.error('[useExperienceSync] Failed to connect:', error);
      });
    }

    // Cleanup on unmount or sessionId change
    return () => {
      sync.disconnect();
      syncRef.current = null;
    };
  }, [sessionId, topicId, schema, isHost, autoConnect]);

  // Request snapshot when customer connects (non-host)
  React.useEffect(() => {
    if (isConnected && !isHost && syncRef.current) {
      // Small delay to ensure host has set up listeners
      const timeoutId = setTimeout(() => {
        logger.log('[useExperienceSync] Requesting snapshot from host');
        syncRef.current?.requestSnapshot();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isConnected, isHost]);

  const send = React.useCallback((data: Partial<T>) => {
    // Always update local state so UI responds immediately even without a session
    setState((prev) => {
      const newState = { ...prev, ...data };
      onStateChangeRef.current?.(newState);
      return newState;
    });

    // Send to peers only when connected
    if (!syncRef.current?.isConnected()) {
      return;
    }
    syncRef.current.send(data);
  }, []);

  const connect = React.useCallback(async () => {
    if (!syncRef.current) {
      logger.warn('[useExperienceSync] Cannot connect: no session');
      return undefined;
    }
    return syncRef.current.connect();
  }, []);

  const disconnect = React.useCallback(() => {
    syncRef.current?.disconnect();
  }, []);

  return {
    state,
    send,
    isConnected,
    connectionId,
    connect,
    disconnect,
    setLocalState: setState,
  };
}
